import { useState, useEffect, useRef } from 'react';
import { getVehiculos, getVehiculoById, getTiposVehiculo, createVehiculo, updateVehiculo } from '../../../shared/helpers/api/mantenedores/vehiculos';

/**
 * Hook para manejar el mantenedor de vehículos
 * Maneja paginación, filtros, live search y llamadas a la API
 */

const INITIAL_PAGE_SIZE = 10;

export const useVehiculosMantenedor = (user, permisos) => {
  const [vehiculos, setVehiculos] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(INITIAL_PAGE_SIZE);
  const [searchValue, setSearchValue] = useState('');
  const [tipoVehiculoId, setTipoVehiculoId] = useState(null);
  const [selectedVehiculo, setSelectedVehiculo] = useState(null);
  const [vehiculoDetalle, setVehiculoDetalle] = useState(null);
  const [detalleLoading, setDetalleLoading] = useState(false);
  const [tipos, setTipos] = useState([]);
  const [tiposLoading, setTiposLoading] = useState(false);

  // Estados para modal
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('view'); // 'view', 'edit', 'create'

  // Control de cancelación de requests
  const abortControllerRef = useRef();
  const lastFetchId = useRef(0);
  const searchTimeoutRef = useRef();

  // Cargar tipos de vehículos al inicializar
  useEffect(() => {
    const fetchTipos = async () => {
      if (user && tipos.length === 0) {
        setTiposLoading(true);
        try {
          const response = await getTiposVehiculo(user);
          if (response.success) {
            setTipos(response.data || []);
          }
        } catch (err) {
          console.error('Error al obtener tipos de vehículo:', err);
        } finally {
          setTiposLoading(false);
        }
      }
    };
    fetchTipos();
  }, [user, tipos.length]);

  // Efecto principal para cargar vehículos
  useEffect(() => {
    // Cancelar request anterior
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Si hay búsqueda activa y es muy corta, no buscar
    if (searchValue.length > 0 && searchValue.length < 3) {
      setVehiculos([]);
      setTotal(0);
      return;
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;
    const fetchId = ++lastFetchId.current;

    setLoading(true);
    setError(null);

    const fetchData = async () => {
      try {
        const params = {
          pageSize,
          pageNumber,
          searchValue: searchValue.length >= 3 ? searchValue : '',
          tipo_vehiculo_id: tipoVehiculoId
        };

        const res = await getVehiculos(user, params);
        
        // Solo actualizar si es la última request
        if (lastFetchId.current === fetchId && !controller.signal.aborted) {
          if (res.success) {
            setVehiculos(res.data);
            setTotal(res.total);
          } else {
            setVehiculos([]);
            setTotal(0);
            setError(res.error);
          }
        }
      } catch (err) {
        if (lastFetchId.current === fetchId && !controller.signal.aborted) {
          setVehiculos([]);
          setTotal(0);
          setError('Error al obtener vehículos');
        }
      }
      
      if (lastFetchId.current === fetchId && !controller.signal.aborted) {
        setLoading(false);
      }
    };

    // Debounce para búsquedas
    if (searchValue.length >= 3) {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      searchTimeoutRef.current = setTimeout(fetchData, 300);
    } else {
      fetchData();
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [user, pageSize, pageNumber, searchValue, tipoVehiculoId]);

  // Handlers para cambios de filtros
  const handleSearchChange = (value) => {
    setSearchValue(value);
    setPageNumber(1);
  };

  const handleTipoVehiculoChange = (tipoId) => {
    setTipoVehiculoId(tipoId);
    setPageNumber(1);
  };

  const handlePageChange = (page) => {
    setPageNumber(page);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setPageNumber(1);
  };

  // Funciones para manejar vehículos
  const fetchVehiculoDetalle = async (id) => {
    setDetalleLoading(true);
    setVehiculoDetalle(null);
    try {
      const res = await getVehiculoById(user, id);
      if (res.success) {
        setVehiculoDetalle(res.data);
      } else {
        setVehiculoDetalle(null);
      }
    } catch (err) {
      setVehiculoDetalle(null);
    }
    setDetalleLoading(false);
  };

  const handleCreateVehiculo = async (data) => {
    setDetalleLoading(true);
    try {
      const res = await createVehiculo(user, data);
      if (res.success) {
        refreshVehiculos();
        setShowModal(false);
        setVehiculoDetalle(null);
      }
      return res;
    } finally {
      setDetalleLoading(false);
    }
  };

  const handleEditVehiculo = async (id, data) => {
    setDetalleLoading(true);
    try {
      const res = await updateVehiculo(user, id, data);
      if (res.success) {
        refreshVehiculos();
        fetchVehiculoDetalle(id);
      }
      return res;
    } finally {
      setDetalleLoading(false);
    }
  };

  const handleDeleteVehiculo = async (id) => {
    // Implementar cuando esté disponible la API
    console.log('Eliminar vehículo:', id);
  };

  // Funciones para modal
  const handleOpenCreateModal = () => {
    setModalMode('create');
    setVehiculoDetalle(null);
    setShowModal(true);
  };

  const handleOpenEditModal = (vehiculo) => {
    setModalMode('edit');
    setSelectedVehiculo(vehiculo);
    setVehiculoDetalle(vehiculo);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedVehiculo(null);
    setVehiculoDetalle(null);
    setModalMode('view');
  };

  // Función para refrescar datos
  const refreshVehiculos = () => {
    setPageNumber(current => current);
  };

  return {
    // Datos
    vehiculos,
    total,
    loading,
    error,
    tipos,
    tiposLoading,
    
    // Estados de filtros
    pageNumber,
    pageSize,
    searchValue,
    tipoVehiculoId,
    
    // Estados de detalle/modal
    selectedVehiculo,
    setSelectedVehiculo,
    vehiculoDetalle,
    detalleLoading,
    showModal,
    modalMode,
    
    // Handlers
    handleSearchChange,
    handleTipoVehiculoChange,
    handlePageChange,
    handlePageSizeChange,
    handleCreateVehiculo,
    handleEditVehiculo,
    handleDeleteVehiculo,
    handleOpenCreateModal,
    handleOpenEditModal,
    handleCloseModal,
    fetchVehiculoDetalle,
    refreshVehiculos
  };
};

export default useVehiculosMantenedor;
